import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  MaintenanceTable,
  TableCell,
  TableCellValue,
  TableCellLabel,
  MaintenanceItemContainer,
  ButtonContainer,
  EditButton,
  AttachmentContainer,
  AttachmentItem,
  AttachmentInfo,
  AttachmentButtons,
  AttachmentButton,
  EmptyProductsMessage,
} from './style'
import { BlurBackground } from '../style'
import cross from '../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../assets/edit-pen-white.svg'

interface ViewFashionOrderModalProps {
  orderId: number
  onClose: () => void
}

interface FashionOrderProductItemInput {
  colorId: number
  size: string
  quantity: number
}

interface Attachment {
  id: number
  originalName: string
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
}

interface BackendOrderProduct {
  id: number
  fashionProductId: number
  discount: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionProduct: {
    id: number
    name: string
    price: number
    group: {
      id: number
      name: string
      family: {
        id: number
        name: string
      }
    }
  }
}

interface BackendFashionOrder {
  id: number
  orderCode: string
  companyId: number
  supplierId: number
  sellerId: number
  orderType: string
  paymentCondition: { name: string } | string
  shippingType: { name: string } | string
  trackingCode: string
  shippingCost: number
  totalCost: number
  startDateOfDelivery: string
  finalDateOfDelivery: string
  extraInformation: string
  quantityItems: string
  quantityPieces: string
  deliveryTime: string
  maximumBillingAmount: number
  createdAt: string
  attachments?: Attachment[]
  company: {
    id: number
    companyName: string
  }
  supplier: {
    id: number
    companyName: string
  }
  seller: {
    id: number
    name: string
  }
  products: BackendOrderProduct[]
}

interface OrderDetailItem {
  label: string
  value: string | React.ReactNode
}

interface OrderProductItem {
  name: string
  collection: string
  group: string
  colors: string
  totalItems: string
  price: string
  discount: string
  subtotal: string
}

interface FullOrderData {
  general: OrderDetailItem[]
  delivery: OrderDetailItem[]
  financial: OrderDetailItem[]
  products: OrderProductItem[]
}

const ViewFashionOrderModal: React.FC<ViewFashionOrderModalProps> = ({
  orderId,
  onClose,
}) => {
  const navigate = useNavigate()

  const [orderData, setOrderData] = useState<FullOrderData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }, [])

  const formatDate = useCallback(
    (isoDateString: string | null | undefined): string => {
      if (!isoDateString) return 'N/A'
      const date = new Date(isoDateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${day}/${month}/${year}`
    },
    [],
  )

  const calculateDiscountValue = useCallback(
    (subtotal: number, discountPercentage: number): number => {
      return (subtotal * discountPercentage) / 100
    },
    [],
  )

  const safeValue = useCallback((value: any): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }
    return String(value)
  }, [])

  const extractValue = useCallback(
    (field: any): string => {
      if (typeof field === 'object' && field !== null && field.name) {
        return field.name
      }
      return safeValue(field)
    },
    [safeValue],
  )

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }, [])

  const isImageFile = useCallback((mimeType: string): boolean => {
    return mimeType.startsWith('image/')
  }, [])

  const isPdfFile = useCallback((mimeType: string): boolean => {
    return mimeType === 'application/pdf'
  }, [])

  const isTextFile = useCallback((mimeType: string): boolean => {
    const textTypes = [
      'text/plain',
      'text/html',
      'text/css',
      'text/javascript',
      'text/csv',
      'text/xml',
      'application/json',
      'application/xml',
      'text/markdown',
      'text/rtf',
    ]
    return textTypes.includes(mimeType) || mimeType.startsWith('text/')
  }, [])

  const canPreview = useCallback(
    (mimeType: string): boolean => {
      return (
        isImageFile(mimeType) || isPdfFile(mimeType) || isTextFile(mimeType)
      )
    },
    [isImageFile, isPdfFile, isTextFile],
  )

  const handleAttachmentView = useCallback(
    async (attachment: Attachment) => {
      if (!canPreview(attachment.mimeType)) {
        alert(
          'Tipo de arquivo não suportado para visualização. Use o botão de download.',
        )
        return
      }

      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachment.id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          },
        )

        const blob = new Blob([response.data], { type: attachment.mimeType })
        const url = window.URL.createObjectURL(blob)

        if (isTextFile(attachment.mimeType)) {
          window.open(url, '_blank')
        } else {
          window.open(url, '_blank')
        }

        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 10000)
      } catch (error) {
        console.error('Erro ao visualizar anexo:', error)
        alert('Erro ao visualizar o arquivo. Tente novamente.')
      }
    },
    [orderId, canPreview, isTextFile],
  )

  const handleAttachmentDownload = useCallback(
    async (attachment: Attachment) => {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachment.id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          },
        )

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', attachment.originalName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erro ao baixar anexo:', error)
        alert('Erro ao baixar o arquivo. Tente novamente.')
      }
    },
    [orderId],
  )

  const renderAttachments = useCallback(
    (attachments: Attachment[] | undefined): React.ReactNode => {
      if (!attachments || attachments.length === 0) {
        return <span>Nenhum anexo</span>
      }

      return (
        <AttachmentContainer>
          {attachments.map((attachment) => (
            <AttachmentItem key={attachment.id}>
              <span>
                {attachment.originalName}{' '}
                <AttachmentInfo>
                  ({formatFileSize(attachment.fileSize)})
                </AttachmentInfo>
              </span>
              <AttachmentButtons>
                {canPreview(attachment.mimeType) && (
                  <AttachmentButton
                    onClick={() => handleAttachmentView(attachment)}
                  >
                    Visualizar
                  </AttachmentButton>
                )}
                <AttachmentButton
                  onClick={() => handleAttachmentDownload(attachment)}
                >
                  Download
                </AttachmentButton>
              </AttachmentButtons>
            </AttachmentItem>
          ))}
        </AttachmentContainer>
      )
    },
    [
      handleAttachmentView,
      handleAttachmentDownload,
      formatFileSize,
      canPreview,
    ],
  )

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('ID do pedido não fornecido')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) {
        setError('Sessão expirada. Faça login novamente.')
        setLoading(false)
        navigate('/login')
        return
      }

      try {
        const response = await axios.get<BackendFashionOrder>(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )

        const orderData = response.data

        if (!orderData) {
          setError('Pedido não encontrado')
          return
        }

        const mappedGeneral: OrderDetailItem[] = [
          { label: 'Código do Pedido', value: safeValue(orderData.orderCode) },
          {
            label: 'Cliente',
            value: safeValue(orderData.company?.companyName),
          },
          {
            label: 'Fornecedor',
            value: safeValue(orderData.supplier?.companyName),
          },
          { label: 'Vendedor', value: safeValue(orderData.seller?.name) },
          { label: 'Tipo de Pedido', value: safeValue(orderData.orderType) },
          { label: 'Data de Criação', value: formatDate(orderData.createdAt) },
        ]

        const mappedDelivery: OrderDetailItem[] = [
          {
            label: 'Tipo de Frete',
            value: extractValue(orderData.shippingType),
          },
          {
            label: 'Código de Rastreio',
            value: safeValue(orderData.trackingCode),
          },
          {
            label: 'Data Início da Entrega',
            value: formatDate(orderData.startDateOfDelivery),
          },
          {
            label: 'Data Final da Entrega',
            value: formatDate(orderData.finalDateOfDelivery),
          },
          { label: 'Anexo', value: renderAttachments(orderData.attachments) },
          {
            label: 'Informações Adicionais',
            value: safeValue(orderData.extraInformation),
          },
        ]

        const mappedFinancial: OrderDetailItem[] = [
          {
            label: 'Condição de Pagamento',
            value: extractValue(orderData.paymentCondition),
          },
          {
            label: 'Custo do Frete',
            value: formatCurrency(Number(orderData.shippingCost) || 0),
          },
          {
            label: 'Quantidade de Peças',
            value: safeValue(orderData.quantityPieces),
          },
          {
            label: 'Valor Máximo de Faturamento',
            value: formatCurrency(Number(orderData.maximumBillingAmount) || 0),
          },
          {
            label: 'Quantidade de Itens',
            value: safeValue(orderData.quantityItems),
          },
          {
            label: 'Total do Pedido',
            value: formatCurrency(Number(orderData.totalCost) || 0),
          },
        ]

        const mappedProducts: OrderProductItem[] = (
          orderData.products || []
        ).map((product) => {
          const uniqueColors =
            product.items && Array.isArray(product.items)
              ? new Set(
                  product.items.map(
                    (item: FashionOrderProductItemInput) => item.colorId,
                  ),
                )
              : new Set()
          const colorCount = uniqueColors.size

          let discountDisplay = '-'
          if (product.discount && product.discount > 0) {
            const originalSubtotal =
              product.subtotal / (1 - product.discount / 100)
            const discountValue = calculateDiscountValue(
              originalSubtotal,
              product.discount,
            )
            discountDisplay = `${product.discount}% (${formatCurrency(
              discountValue,
            )})`
          }

          return {
            name: safeValue(product.fashionProduct?.name),
            collection: safeValue(product.fashionProduct?.group?.family?.name),
            group: safeValue(product.fashionProduct?.group?.name),
            colors: colorCount.toString(),
            totalItems: (product.totalItems || 0).toString(),
            price: formatCurrency(Number(product.fashionProduct?.price) || 0),
            discount: discountDisplay,
            subtotal: formatCurrency(Number(product.subtotal) || 0),
          }
        })

        setOrderData({
          general: mappedGeneral,
          delivery: mappedDelivery,
          financial: mappedFinancial,
          products: mappedProducts,
        })
      } catch (err) {
        console.error('Erro ao buscar o pedido:', err)
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            setError('Não autorizado. Por favor, faça login novamente.')
            navigate('/login')
          } else if (err.response?.status === 404) {
            setError('Pedido não encontrado.')
          } else {
            setError('Erro ao carregar dados do pedido. Tente novamente.')
          }
        } else {
          setError('Erro de conexão. Verifique sua internet.')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [
    orderId,
    navigate,
    formatCurrency,
    formatDate,
    calculateDiscountValue,
    safeValue,
    extractValue,
    renderAttachments,
  ])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])
  const handleEditOrder = useCallback(() => {
    navigate(`/panel/fashion/edit/${orderId}`)
    onClose()
  }, [navigate, orderId, onClose])

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados do pedido...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Carregando informações do pedido...</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p style={{ color: 'red' }}>{error}</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!orderData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Pedido não encontrado</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Nenhum dado encontrado para o pedido com ID: {orderId}.</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const orderCodeItem = orderData.general.find(
    (item) => item.label === 'Código do Pedido',
  )
  const displayCode =
    orderCodeItem?.value !== 'N/A' && orderCodeItem?.value
      ? orderCodeItem.value
      : `PF-${String(orderId).padStart(5, '0')}`

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Pedido Fashion: {displayCode}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {orderData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Entrega</SectionHeader>
            <DetailGrid>
              {orderData.delivery.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Informações financeiras</SectionHeader>
            <DetailGrid>
              {orderData.financial.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Produtos do pedido</SectionHeader>
            <MaintenanceTable>
              {orderData.products.length > 0 ? (
                orderData.products.map((product, index) => (
                  <MaintenanceItemContainer key={index}>
                    <TableCell>
                      <TableCellLabel>Produto</TableCellLabel>
                      <TableCellValue>{product.name}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Coleção</TableCellLabel>
                      <TableCellValue>{product.collection}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Grupo</TableCellLabel>
                      <TableCellValue>{product.group}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Cores</TableCellLabel>
                      <TableCellValue>{product.colors}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Qtd. Peças</TableCellLabel>
                      <TableCellValue>{product.totalItems}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Preço Unit.</TableCellLabel>
                      <TableCellValue>{product.price}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Desconto</TableCellLabel>
                      <TableCellValue>{product.discount}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Subtotal</TableCellLabel>
                      <TableCellValue>{product.subtotal}</TableCellValue>
                    </TableCell>
                  </MaintenanceItemContainer>
                ))
              ) : (
                <EmptyProductsMessage>
                  Nenhum produto encontrado neste pedido.
                </EmptyProductsMessage>
              )}
            </MaintenanceTable>
          </Section>
        </ContentContainer>

        <ButtonContainer>
          <EditButton onClick={handleEditOrder}>
            <img src={editIcon} alt='Editar' />
            Editar pedido
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
  )
}

export default ViewFashionOrderModal
